import type { EChartsOption } from "echarts";
import type {
  ChartSpec,
  ChartResult,
  ChartResultRow,
  ChartDimension,
  ChartMetric,
} from "../../shared/src/protocol";

export interface ThemeColors {
  primary: string;
  danger: string;
  border: string;
  text: string;
  textMuted: string;
}

const PALETTE = [
  "#3366cc", "#dc3912", "#ff9900", "#109618", "#990099",
  "#0099c6", "#dd4477", "#66aa00", "#b82e2e", "#316395",
];

const TIME_ORDERED: ReadonlyArray<ChartDimension> = ["time", "hourOfDay", "dayOfWeek"];
const MAX_CHART_KEYS = 30;

function metricValue(row: ChartResultRow, metric: ChartMetric): number {
  if (metric === "cases") return row.cases;
  if (metric === "deaths") return row.deaths;
  return row.cases > 0 ? Math.round((row.deaths / row.cases) * 1000) / 10 : 0;
}

export function specToOption(
  spec: ChartSpec,
  result: ChartResult,
  theme: ThemeColors,
  metricLabel: string,
  dimensionLabel: string,
): EChartsOption {
  const { rows } = result;
  if (rows.length === 0) {
    return { title: { show: false } };
  }

  const keyMap = new Map<string, string>();
  const seriesMap = new Map<string, string>();
  for (const r of rows) {
    if (!keyMap.has(r.key)) keyMap.set(r.key, r.keyLabel);
    if (spec.breakdown && r.series !== undefined && !seriesMap.has(r.series)) {
      seriesMap.set(r.series, r.seriesLabel ?? r.series);
    }
  }

  let keys = [...keyMap.keys()];
  if (TIME_ORDERED.includes(spec.dimension)) {
    keys.sort((a, b) => a.localeCompare(b));
  } else {
    const totals = new Map<string, number>();
    for (const r of rows) {
      totals.set(r.key, (totals.get(r.key) ?? 0) + metricValue(r, spec.metric));
    }
    keys.sort((a, b) => (totals.get(b) ?? 0) - (totals.get(a) ?? 0));
    if (keys.length > MAX_CHART_KEYS && spec.chartType !== "pie") {
      keys = keys.slice(0, MAX_CHART_KEYS);
    }
  }
  const keyLabels = keys.map((k) => keyMap.get(k) ?? k);

  const seriesKeys = [...seriesMap.keys()].sort();
  const seriesLabels = seriesKeys.map((s) => seriesMap.get(s) ?? s);
  const seriesIdx = new Map(seriesKeys.map((s, i) => [s, i]));
  const keyIdx = new Map(keys.map((k, i) => [k, i]));

  const yFormatter =
    spec.metric === "mortalityPct" ? "{value}%" : "{value}";

  if (spec.chartType === "pie") {
    const totals = new Map<string, number>();
    for (const r of rows) {
      totals.set(r.key, (totals.get(r.key) ?? 0) + metricValue(r, spec.metric));
    }
    const data = keys.map((k, i) => ({
      name: keyLabels[i],
      value: totals.get(k) ?? 0,
    }));
    return {
      tooltip: { trigger: "item", formatter: "{b}: <b>{c}</b> ({d}%)" },
      legend: {
        bottom: 0,
        textStyle: { color: theme.textMuted, fontSize: 11 },
      },
      series: [
        {
          name: metricLabel,
          type: "pie",
          radius: ["45%", "70%"],
          center: ["50%", "45%"],
          label: { color: theme.text, fontSize: 11 },
          labelLine: { lineStyle: { color: theme.border } },
          data,
        },
      ],
    };
  }

  if (spec.chartType === "heatmap") {
    if (!spec.breakdown) {
      return specToOption(
        { ...spec, chartType: "bar" },
        result,
        theme,
        metricLabel,
      );
    }
    const points: Array<[number, number, number]> = [];
    let max = 0;
    for (const r of rows) {
      const xi = keyIdx.get(r.key);
      const yi = r.series !== undefined ? seriesIdx.get(r.series) : undefined;
      if (xi === undefined || yi === undefined) continue;
      const v = metricValue(r, spec.metric);
      points.push([xi, yi, v]);
      if (v > max) max = v;
    }
    return {
      tooltip: {
        position: "top",
        formatter: (params: { value: [number, number, number] }) => {
          const [xi, yi, v] = params.value;
          return `${keyLabels[xi]} / ${seriesLabels[yi]}<br/><b>${v}${spec.metric === "mortalityPct" ? "%" : ""}</b>`;
        },
      },
      grid: { left: 90, right: 12, top: 12, bottom: 64 },
      xAxis: {
        type: "category",
        data: keyLabels,
        name: dimensionLabel,
        nameLocation: "middle",
        nameGap: 38,
        nameTextStyle: { fontSize: 10, color: theme.textMuted, fontWeight: 600 },
        axisLabel: { fontSize: 9, color: theme.textMuted, interval: 0, rotate: 35 },
        axisLine: { lineStyle: { color: theme.border } },
      },
      yAxis: {
        type: "category",
        data: seriesLabels,
        axisLabel: { fontSize: 9, color: theme.textMuted },
        axisLine: { lineStyle: { color: theme.border } },
      },
      visualMap: {
        min: 0,
        max: max || 1,
        calculable: true,
        orient: "horizontal",
        left: "center",
        bottom: 0,
        textStyle: { fontSize: 9, color: theme.textMuted },
        inRange: { color: ["#f5f5f5", theme.primary, theme.danger] },
      },
      series: [
        {
          name: metricLabel,
          type: "heatmap",
          data: points,
          label: { show: false },
        },
      ],
    };
  }

  const isLine = spec.chartType === "line";
  const isStacked = spec.chartType === "stackedBar";

  type SeriesEntry = { name: string; data: (number | null)[]; color: string };
  const seriesEntries: SeriesEntry[] = [];

  if (spec.breakdown) {
    for (let i = 0; i < seriesKeys.length; i++) {
      const sKey = seriesKeys[i];
      const sLabel = seriesLabels[i];
      const data: (number | null)[] = new Array(keys.length).fill(null);
      seriesEntries.push({
        name: sLabel,
        data,
        color: PALETTE[i % PALETTE.length],
      });
    }
    for (const r of rows) {
      const xi = keyIdx.get(r.key);
      const si = r.series !== undefined ? seriesIdx.get(r.series) : undefined;
      if (xi === undefined || si === undefined) continue;
      seriesEntries[si].data[xi] = metricValue(r, spec.metric);
    }
  } else {
    const data: (number | null)[] = new Array(keys.length).fill(null);
    const totals = new Map<string, { cases: number; deaths: number }>();
    for (const r of rows) {
      const cur = totals.get(r.key) ?? { cases: 0, deaths: 0 };
      cur.cases += r.cases;
      cur.deaths += r.deaths;
      totals.set(r.key, cur);
    }
    for (let i = 0; i < keys.length; i++) {
      const t = totals.get(keys[i]) ?? { cases: 0, deaths: 0 };
      data[i] = metricValue(
        { key: keys[i], keyLabel: keyLabels[i], cases: t.cases, deaths: t.deaths },
        spec.metric,
      );
    }
    seriesEntries.push({
      name: metricLabel,
      data,
      color:
        spec.metric === "deaths" || spec.metric === "mortalityPct"
          ? theme.danger
          : theme.primary,
    });
  }

  const series = seriesEntries.map((e) => ({
    name: e.name,
    type: isLine ? "line" : "bar",
    stack: isStacked ? "total" : undefined,
    data: e.data,
    itemStyle: { color: e.color },
    lineStyle: isLine ? { color: e.color, width: 2 } : undefined,
    symbolSize: isLine ? 4 : undefined,
    smooth: isLine ? true : undefined,
    connectNulls: isLine ? true : undefined,
  }));

  const nameStyle = { fontSize: 10, color: theme.textMuted, fontWeight: 600 };
  // Leave extra room below the grid when a breakdown legend is present so the
  // legend has space to wrap onto multiple lines without colliding with the
  // x-axis labels.
  const gridBottom = spec.breakdown
    ? keyLabels.length > 12
      ? 80
      : 68
    : keyLabels.length > 12
      ? 56
      : 44;
  return {
    grid: {
      left: 56,
      right: 12,
      top: 12,
      bottom: gridBottom,
    },
    tooltip: { trigger: "axis", axisPointer: { type: isLine ? "line" : "shadow" } },
    legend: spec.breakdown
      ? {
          bottom: 4,
          type: "scroll",
          left: "center",
          itemGap: 18,
          itemWidth: 14,
          itemHeight: 8,
          padding: [2, 4],
          pageIconColor: theme.textMuted,
          pageIconInactiveColor: theme.border,
          pageTextStyle: { color: theme.textMuted, fontSize: 10 },
          textStyle: { color: theme.textMuted, fontSize: 10 },
        }
      : { show: false },
    xAxis: {
      type: "category",
      data: keyLabels,
      name: dimensionLabel,
      nameLocation: "middle",
      nameGap: keyLabels.length > 12 ? 36 : 24,
      nameTextStyle: nameStyle,
      axisLabel: {
        fontSize: 9,
        color: theme.textMuted,
        interval: 0,
        rotate: keyLabels.length > 12 ? 35 : 0,
      },
      axisLine: { lineStyle: { color: theme.border } },
      axisTick: { show: false },
    },
    yAxis: {
      type: "value",
      min: 0,
      name: metricLabel,
      nameLocation: "middle",
      nameGap: 40,
      nameTextStyle: nameStyle,
      axisLabel: { fontSize: 9, color: theme.textMuted, formatter: yFormatter },
      splitLine: { lineStyle: { color: theme.border, opacity: 0.4 } },
    },
    series: series as EChartsOption["series"],
  };
}

// ─── Canned charts (data already in InsightsResult) ──────────────────

export type Bucket = { period: string; cases: number; deaths: number };

export function formatPeriodLabel(period: string): string {
  if (period.includes("-W")) return period.slice(5);
  const parts = period.split("-");
  return parts.length === 3 ? `${parts[1]}-${parts[2]}` : parts.slice(1).join("-");
}

function timeAxisFrame(
  buckets: Bucket[],
  labelStep: number,
  theme: ThemeColors,
  xName: string,
  yName: string,
) {
  const labels = buckets.map((b, i) =>
    i % labelStep === 0 || i === buckets.length - 1
      ? formatPeriodLabel(b.period)
      : "",
  );
  const nameStyle = { fontSize: 10, color: theme.textMuted, fontWeight: 600 };
  return {
    grid: { left: 50, right: 12, top: 12, bottom: 64 },
    xAxis: {
      type: "category" as const,
      data: labels,
      name: xName,
      nameLocation: "middle" as const,
      nameGap: 44,
      nameTextStyle: nameStyle,
      axisLabel: {
        fontSize: 9,
        color: theme.textMuted,
        interval: 0,
        rotate: 45,
        hideOverlap: true,
      },
      axisLine: { lineStyle: { color: theme.border } },
      axisTick: { show: false },
    },
    yAxis: {
      type: "value" as const,
      name: yName,
      nameLocation: "middle" as const,
      nameGap: 36,
      nameTextStyle: nameStyle,
      axisLabel: { fontSize: 9, color: theme.textMuted },
      splitLine: { lineStyle: { color: theme.border, opacity: 0.4 } },
    },
  };
}

type TFn = (key: string) => string;

export function timelineOption(
  buckets: Bucket[],
  labelStep: number,
  theme: ThemeColors,
  t: TFn,
): EChartsOption {
  return {
    ...timeAxisFrame(buckets, labelStep, theme, t("insights.time"), t("insights.count")),
    tooltip: {
      trigger: "axis",
      axisPointer: { type: "shadow" },
      formatter: (params: { dataIndex: number }[]) => {
        const b = buckets[params[0]?.dataIndex ?? 0];
        return b
          ? `${b.period}<br/>${t("insights.cases")}: <b>${b.cases}</b><br/>${t("insights.deaths")}: <b>${b.deaths}</b>`
          : "";
      },
    },
    series: [
      {
        name: t("insights.cases"),
        type: "bar",
        stack: "total",
        data: buckets.map((b) => b.cases),
        itemStyle: { color: theme.primary },
        barCategoryGap: "10%",
      },
      {
        name: t("insights.deaths"),
        type: "bar",
        stack: "total",
        data: buckets.map((b) => b.deaths),
        itemStyle: { color: theme.danger },
      },
    ],
  };
}

export function triageDonutOption(
  rows: ReadonlyArray<{ category: string; count: number }>,
  triageColors: Record<string, string>,
  theme: ThemeColors,
  t: TFn,
): EChartsOption | null {
  if (rows.length === 0) return null;
  const sorted = [...rows].sort((a, b) => a.category.localeCompare(b.category));
  return {
    tooltip: { trigger: "item", formatter: "{b}: <b>{c}</b> ({d}%)" },
    legend: { bottom: 0, textStyle: { color: theme.textMuted, fontSize: 11 } },
    series: [
      {
        name: t("insights.triage"),
        type: "pie",
        radius: ["45%", "70%"],
        center: ["50%", "45%"],
        avoidLabelOverlap: true,
        label: { color: theme.text, fontSize: 11, formatter: "{b}\n{c}" },
        labelLine: { lineStyle: { color: theme.border } },
        data: sorted.map((row) => ({
          name: row.category,
          value: row.count,
          itemStyle: { color: triageColors[row.category] || theme.textMuted },
        })),
      },
    ],
  };
}

export function mortalityOption(
  buckets: Bucket[],
  labelStep: number,
  theme: ThemeColors,
  t: TFn,
): EChartsOption {
  const frame = timeAxisFrame(
    buckets,
    labelStep,
    theme,
    t("insights.time"),
    t("insights.mortalityPct"),
  );
  const values = buckets.map((b) =>
    b.cases > 0 ? Math.round((b.deaths / b.cases) * 1000) / 10 : null,
  );
  return {
    ...frame,
    yAxis: {
      ...frame.yAxis,
      min: 0,
      axisLabel: {
        fontSize: 9,
        color: theme.textMuted,
        formatter: "{value}%",
      },
    },
    tooltip: {
      trigger: "axis",
      formatter: (params: { dataIndex: number }[]) => {
        const b = buckets[params[0]?.dataIndex ?? 0];
        if (!b) return "";
        const pct = b.cases > 0 ? ((b.deaths / b.cases) * 100).toFixed(1) : "—";
        return `${b.period}<br/>${t("insights.mortalityPct")}: <b>${pct}%</b><br/>${b.deaths} / ${b.cases}`;
      },
    },
    series: [
      {
        name: t("insights.mortalityPct"),
        type: "line",
        smooth: true,
        symbolSize: 4,
        connectNulls: true,
        data: values,
        itemStyle: { color: theme.danger },
        lineStyle: { color: theme.danger, width: 2 },
        areaStyle: { color: theme.danger, opacity: 0.12 },
      },
    ],
  };
}
